import { useCallback } from "react"
import { useMutation } from "urql"

import "../graphql/salesRequestMutations"

import { SendSalesQuoteEmailDocument } from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

interface SendSalesQuoteEmailParams {
  quoteGroupId: number
  emailReceiverContactIds: string[]
  emailNote: string
}

interface UseSendSalesQuoteEmailReturn {
  sendSalesQuoteEmail: (params: SendSalesQuoteEmailParams) => Promise<boolean>
  isSending: boolean
}

export const useSendSalesQuoteEmail = (): UseSendSalesQuoteEmailReturn => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const [{ fetching }, executeMutation] = useMutation(SendSalesQuoteEmailDocument)

  const sendSalesQuoteEmail = useCallback(
    async ({ quoteGroupId, emailReceiverContactIds, emailNote }: SendSalesQuoteEmailParams): Promise<boolean> => {
      const { data, error } = await executeMutation({
        quoteGroupId,
        emailReceiverContactIds,
        emailNote: emailNote.trim() || null,
      })
      const output = data?.salesPipeline.sendSalesQuoteEmail

      if (error || output?.__typename === "SendSalesQuoteEmailFailure") {
        const message = output?.__typename === "SendSalesQuoteEmailFailure" ? output.error.message : error?.message
        notify({
          type: NotificationType.Error,
          message: message ?? "Failed to send the quote email. Please try again.",
        })
        return false
      }

      notify({
        type: NotificationType.Success,
        message: "Quote sent to customer",
      })
      return true
    },
    [executeMutation, notify]
  )

  return { sendSalesQuoteEmail, isSending: fetching }
}
